
import React from "react";
import TocTocHeader from "@/components/TocTocHeader";
import TocTocFooter from "@/components/TocTocFooter";
import { MessageSquare, Bot, Clock, Users, Check, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";

const TocChat = () => {
  const features = [
    {
      icon: Bot,
      title: "Chatbot Inteligente",
      description: "IA treinada para responder perguntas sobre imóveis e agendar visitas automaticamente"
    },
    {
      icon: Clock,
      title: "Atendimento 24/7",
      description: "Seus clientes podem tirar dúvidas e fazer consultas a qualquer hora do dia"
    },
    {
      icon: Smartphone,
      title: "Multi-plataforma",
      description: "Integração com WhatsApp, Facebook Messenger, Instagram e site"
    },
    {
      icon: Users,
      title: "Gestão Unificada",
      description: "Centralize todas as conversas em uma única interface intuitiva"
    }
  ];

  const benefits = [
    "Aumento de 70% na captação de leads",
    "Redução de 50% no tempo de resposta",
    "Atendimento simultâneo ilimitado",
    "Qualificação automática de prospects",
    "Integração total com CRM",
    "Relatórios detalhados de performance"
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <TocTocHeader />
      <main className="flex-1">
        {/* Hero Section */}
        <section className="py-16 md:py-24 bg-gradient-to-r from-[#261E13] to-[#323031] text-white">
          <div className="container max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in">
                  Toc <span className="text-[#FF5605]">Chat</span>
                </h1>
                <p className="text-xl text-white/90 leading-relaxed mb-8 animate-fade-in animation-delay-200">
                  Sistema de chat integrado com inteligência artificial para atendimento imobiliário. 
                  Capture mais leads, qualifique prospects e nunca perca uma oportunidade.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 animate-fade-in animation-delay-400">
                  <Button className="bg-[#FF5605] hover:bg-[#E14D04] text-white px-8 py-4 rounded-xl text-lg">
                    Teste Grátis por 15 dias
                  </Button>
                  <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#323031] px-8 py-4 rounded-xl text-lg">
                    Ver Demo ao Vivo
                  </Button>
                </div>
              </div>
              <div className="relative">
                <div className="bg-white rounded-3xl p-6 shadow-2xl">
                  <div className="flex items-center space-x-3 mb-4 pb-4 border-b border-gray-100">
                    <div className="w-10 h-10 bg-[#FF5605] rounded-full flex items-center justify-center">
                      <Bot className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <div className="font-semibold text-[#323031]">Assistente Toc</div>
                      <div className="text-xs text-green-500 flex items-center">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                        Online
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="bg-gray-100 rounded-2xl p-3 max-w-xs">
                      <p className="text-sm text-[#323031]">Olá! Tenho interesse no apartamento de 2 quartos.</p>
                    </div>
                    <div className="bg-[#FF5605] text-white rounded-2xl p-3 max-w-xs ml-auto">
                      <p className="text-sm">Perfeito! Posso agendar uma visita para você. Que dia seria melhor?</p>
                    </div>
                    <div className="bg-gray-100 rounded-2xl p-3 max-w-xs">
                      <p className="text-sm text-[#323031]">Amanhã de tarde estaria bom</p>
                    </div>
                    <div className="bg-[#FF5605] text-white rounded-2xl p-3 max-w-xs ml-auto">
                      <p className="text-sm">Agendado para amanhã às 14h! Enviei os detalhes por WhatsApp 📱</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-24 bg-white">
          <div className="container max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#323031] mb-6">
                Recursos <span className="text-[#FF5605]">Inteligentes</span>
              </h2>
              <p className="text-xl text-[#5B5A59] max-w-3xl mx-auto">
                Transforme visitantes em clientes com tecnologia de ponta em atendimento automatizado
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="text-center p-8 rounded-3xl hover:shadow-xl transition-all duration-300 hover:scale-105 border border-gray-100">
                  <div className="w-16 h-16 bg-[#FF5605]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <feature.icon className="h-8 w-8 text-[#FF5605]" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#323031] mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-[#5B5A59] leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-24 bg-[#F6E5C9]/20">
          <div className="container max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-4xl font-bold text-[#323031] mb-8">
                  Resultados <span className="text-[#FF5605]">Comprovados</span>
                </h2>
                <div className="space-y-6">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="flex items-center space-x-4">
                      <div className="w-8 h-8 bg-[#FF5605]/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="h-5 w-5 text-[#FF5605]" />
                      </div>
                      <span className="text-[#5B5A59] text-lg">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-6">
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <div className="text-3xl font-bold text-[#FF5605] mb-2">+247%</div>
                  <div className="text-[#323031] font-semibold mb-1">Aumento em Leads</div>
                  <div className="text-sm text-[#5B5A59]">Comparado ao atendimento tradicional</div>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <div className="text-3xl font-bold text-[#FF5605] mb-2">24/7</div>
                  <div className="text-[#323031] font-semibold mb-1">Disponibilidade</div>
                  <div className="text-sm text-[#5B5A59]">Atendimento contínuo, sem parar</div>
                </div>
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <div className="text-3xl font-bold text-[#FF5605] mb-2">Menos de 2min</div>
                  <div className="text-[#323031] font-semibold mb-1">Tempo de Resposta</div>
                  <div className="text-sm text-[#5B5A59]">Resposta instantânea a qualquer hora</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Integration Section */}
        <section className="py-24 bg-white">
          <div className="container max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#323031] mb-6">
                Integração <span className="text-[#FF5605]">Total</span>
              </h2>
              <p className="text-xl text-[#5B5A59] max-w-3xl mx-auto">
                Conecte todos os seus canais de comunicação em uma única plataforma
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center p-6 bg-green-50 rounded-2xl">
                <div className="w-16 h-16 bg-green-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-semibold text-[#323031]">WhatsApp</h3>
              </div>
              <div className="text-center p-6 bg-blue-50 rounded-2xl">
                <div className="w-16 h-16 bg-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-semibold text-[#323031]">Facebook</h3>
              </div>
              <div className="text-center p-6 bg-purple-50 rounded-2xl">
                <div className="w-16 h-16 bg-purple-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-semibold text-[#323031]">Instagram</h3>
              </div>
              <div className="text-center p-6 bg-orange-50 rounded-2xl">
                <div className="w-16 h-16 bg-[#FF5605] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="h-8 w-8 text-white" />
                </div>
                <h3 className="font-semibold text-[#323031]">Website</h3>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-[#261E13] to-[#323031] text-white">
          <div className="container max-w-7xl mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Nunca mais perca um <span className="text-[#FF5605]">lead</span>
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Comece hoje mesmo a converter mais visitantes em clientes com o Toc Chat
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button className="bg-[#FF5605] hover:bg-[#E14D04] text-white px-12 py-4 rounded-xl text-lg hover:scale-105 transition-transform">
                Começar Teste Grátis
              </Button>
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#323031] px-12 py-4 rounded-xl text-lg">
                Agendar Demonstração
              </Button>
            </div>
          </div>
        </section>
      </main>
      <TocTocFooter />
    </div>
  );
};

export default TocChat;
